/*
  Poster Set Form (Outsiders theme build) - v3

  Each print card renders snippets/product-fsf-picker.liquid (or a plain
  <select> fallback). Each card has one delegated `change` listener and
  resolves its own variant from the variants JSON embedded in the page.
  Every `.poster-set-form__total-price` on the page (headline price and the
  total above the buttons) shows the live set total. "Add set to cart" goes
  to the cart; "Buy set now" goes straight to checkout.
*/

function formatMoney(cents, format) {
  if (typeof cents !== 'number') return '';
  const value = (cents / 100).toFixed(2);
  const [whole, decimal] = value.split('.');
  const withCommas = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  if (!format) return `${withCommas}.${decimal}`;

  return format
    .replace(/\{\{\s*amount\s*\}\}/, `${withCommas}.${decimal}`)
    .replace(/\{\{\s*amount_no_decimals\s*\}\}/, withCommas)
    .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, `${withCommas},${decimal}`);
}

function currentValueFor(print, optionName) {
  const checkedRadio = print.querySelector(
    `input[name="${CSS.escape(optionName)}"]:checked`,
  );
  if (checkedRadio) return checkedRadio.value;

  const select = print.querySelector(`select[name="${CSS.escape(optionName)}"]`);
  return select ? select.value : null;
}

class PosterSetForm extends HTMLElement {
  connectedCallback() {
    this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
    this.prints = Array.from(this.querySelectorAll('.poster-set-form__print'));
    this.submitButton = this.querySelector('.poster-set-form__submit');
    this.buyNowButton = this.querySelector('.poster-set-form__buy-now');
    this.submitError = this.querySelector('.poster-set-form__submit-error');
    this.totalPrices = Array.from(this.querySelectorAll('.poster-set-form__total-price'));

    this.prints.forEach((print) => {
      const variantsJson = print.querySelector('.poster-set-form__variants-data');
      print.variants = JSON.parse(variantsJson.textContent);
      print.optionNames = print.dataset.optionNames.split('|');
      print.selectedPrice = null;

      print.addEventListener('change', () => this.resolveVariant(print));
      this.resolveVariant(print);
    });

    if (this.submitButton) {
      this.submitButton.addEventListener('click', () => this.addSetToCart('/cart'));
    }
    if (this.buyNowButton) {
      this.buyNowButton.addEventListener('click', () => this.addSetToCart('/checkout'));
    }
  }

  get actionButtons() {
    return [this.submitButton, this.buyNowButton].filter(Boolean);
  }

  resolveVariant(print) {
    const selections = print.optionNames.map((name) => currentValueFor(print, name));

    const variant =
      selections.every((value) => value !== null) &&
      print.variants.find((v) => {
        const variantOptions = [v.option1, v.option2, v.option3];
        return selections.every((value, index) => variantOptions[index] === value);
      });

    const hiddenInput = print.querySelector('.poster-set-form__variant-id');
    const unavailableMessage = print.querySelector('.poster-set-form__unavailable');
    const priceRegular = print.querySelector('.poster-set-form__price .price-item--regular');

    if (variant && variant.available) {
      hiddenInput.value = variant.id;
      print.selectedPrice = variant.price;
      unavailableMessage.hidden = true;

      if (priceRegular) {
        // shop.money_format contains markup on this store, so parse as HTML.
        priceRegular.innerHTML = formatMoney(variant.price, this.moneyFormat);
      }
    } else {
      hiddenInput.value = '';
      print.selectedPrice = null;
      unavailableMessage.hidden = !selections.every((value) => value !== null);
    }

    this.updateSubmitState();
  }

  updateSubmitState() {
    const allSelected = this.prints.every(
      (print) => print.querySelector('.poster-set-form__variant-id').value,
    );

    this.actionButtons.forEach((button) => {
      button.disabled = !allSelected;
    });

    if (allSelected) {
      const total = this.prints.reduce((sum, print) => sum + print.selectedPrice, 0);
      const formatted = formatMoney(total, this.moneyFormat);
      this.totalPrices.forEach((el) => {
        el.innerHTML = formatted;
      });
    } else {
      this.totalPrices.forEach((el) => {
        el.textContent = 'Choose options for each print';
      });
    }
  }

  async addSetToCart(redirectTo) {
    this.submitError.hidden = true;

    const variantIds = this.prints.map(
      (print) => print.querySelector('.poster-set-form__variant-id').value,
    );

    if (variantIds.some((id) => !id)) {
      this.submitError.textContent = 'Please choose an available option for every print.';
      this.submitError.hidden = false;
      return;
    }

    const setId = `set-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

    const items = variantIds.map((id) => ({
      id: Number(id),
      quantity: 1,
      properties: {_set: setId, _set_size: String(variantIds.length)},
    }));

    this.actionButtons.forEach((button) => {
      button.disabled = true;
    });

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({items}),
      });

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.description || body.message || 'Could not add the set to your cart.');
      }

      window.location.href = redirectTo;
    } catch (error) {
      this.submitError.textContent = error.message;
      this.submitError.hidden = false;
      this.actionButtons.forEach((button) => {
        button.disabled = false;
      });
    }
  }
}

customElements.define('poster-set-form', PosterSetForm);
